import { channelId, storeHash, snackbar } from '@/utils';

import B3Request from '../../request/b3Fetch';
import { RequestType } from '../../request/base';
import { ASCAccount } from '@/pages/AccountSetting/components';

const { VITE_ROCKETFLOWZ_BASE_URL, VITE_APP_CLIENT_ID } = import.meta.env;

interface UploadFileData {
  file: File;
  type: string;
}

export const uploadB2BFile = (data: UploadFileData) => {
  const { file, type } = data;

  const formData = new FormData();
  formData.append('mediaFile', file);
  formData.append('requestType', type); // companyAttachedFile,quoteAttachedFile

  return B3Request.fileUpload('/api/v2/media/upload', formData);
};

export const setChannelStoreType = () =>
  B3Request.put('/api/v2/store-configs/channel-storefront-type', RequestType.B2BRest, {
    bcChannelId: channelId,
    storefrontType: 1,
    storeHash,
  });

export const validateQuoteExtraFields = (data: CustomFieldItems) =>
  B3Request.post('/api/v2/extra-fields/quote/validate', RequestType.B2BRest, {
    ...data,
    storeHash,
  });

export const getJwtToken = async () => {
  const clientId = VITE_APP_CLIENT_ID;
  const tokenResponse = await fetch(`/customer/current.jwt?app_client_id=${clientId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });
  const tokenData = await tokenResponse.json();
  return tokenData.token;
}

export const customerCompanies = async () => {
  const jwtToken = await getJwtToken();
  try {
    const response = await fetch(`${VITE_ROCKETFLOWZ_BASE_URL}/api/v1/asc/customer/assigned-companies`, {
      headers: {
        'X-BC-JWT-TOKEN': jwtToken
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    var result = await response.json();
    return result.companiesData;
  } catch (error) {
    console.error(`Error fetching customer companies`, error);
    return '';
  }
}

export const getASCAccounts = async (customerId: number) => {
  const jwtToken = await getJwtToken();
  if (!jwtToken) {
    console.error('JWT token not available');
    return '';
  }

  try {
    const response = await fetch(
      `${VITE_ROCKETFLOWZ_BASE_URL}/api/v1/asc/accounts/${customerId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-BC-JWT-TOKEN': jwtToken
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    snackbar.error(`Error fetching accounts for customer ${customerId}:`);
    return '';
  }
}

export const postASCAccount = async (payload: {
  account: ASCAccount;
  customer: {}
}) => {
  const jwtToken = await getJwtToken();
  if (!jwtToken) {
    console.error('JWT token not available');
    return '';
  }
  try {
    const response = await fetch(`${VITE_ROCKETFLOWZ_BASE_URL}/api/v1/asc/accounts/create`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-BC-JWT-TOKEN': jwtToken
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    return data;
  } catch (error) {
    snackbar.error('Failed to save ASC accounts');
    throw error;
  }
};

export const itemPrices = async (accountNumber: string, sku: string) => {
  const url = `${VITE_ROCKETFLOWZ_BASE_URL}/api/v1/itemprices/${accountNumber}`;
  const jwtToken = await getJwtToken();
  if (!jwtToken) {
    console.error('JWT token not available');
    return '';
  }
  try {
    const itemData = {
      item_number: sku,
      upc: '',
      figure_number: ''
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-BC-JWT-TOKEN': jwtToken
      },
      body: JSON.stringify({ 'item-data' : itemData })
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    var result = await response.json();
    return result;
  }
  catch (error) {
    console.error(`Error fetching item prices for account ${accountNumber}`, error);
    return '';
  }
}